import {
  Alert,
  Badge,
  Button,
  Card,
  Container,
  Divider,
  Grid,
  Group,
  Loader,
  Modal,
  Paper,
  Select,
  Stack,
  Table,
  Tabs,
  Text,
  Title
} from '@mantine/core';
import { IconCreditCard, IconHistory, IconInfoCircle } from '@tabler/icons-react';
import { QRCodeSVG } from 'qrcode.react';
import React from 'react';
import { useAuthContext } from '../components/AuthProvider';
import { useTokenManagement } from '../hooks/useTokenManagement';
import { PaymentStatus } from '../types/payment.types';

export function TokensPage() {
  const {
    // State data
    filteredPackages,
    userToken,
    isLoading,
    error,
    paymentData,
    transactions,
    pagination,
    activeTab,
    paymentModalOpen,
    successModalOpen,
    updateStatusModal,
    
    // Formatting functions
    formatTokenInfo,
    formatPrice,
    getStatusColor,
    formatStatus,
    
    // Handlers and functions
    setActiveTab,
    handleBuyPackage,
    openUpdateStatusModal,
    handleUpdateStatus,
    fetchTransactions,
    clearError,
    closePaymentModal,
    closeUpdateStatusModal,
    updateStatusModalState,
    checkPaymentStatus,
    setSuccessModalOpen
  } = useTokenManagement();

  const { user } = useAuthContext();
  
  const { formattedBalance, formattedExpiry } = formatTokenInfo();

  return (
    <Container size="lg" py="xl">
      <Group justify="space-between" mb="md">
        <Title order={2}>Quản lý Token</Title>
        <Button component="a" href="/" variant="subtle">Trở về trang chủ</Button>
      </Group>

      {error && (
        <Alert color="red" title="Lỗi" withCloseButton onClose={clearError} mb="md">
          {error}
        </Alert>
      )}

      <Paper p="md" withBorder mb="xl">
        <Group justify="space-between">
          <div>
            <Text size="lg" fw={500}>Số token hiện có</Text>
            <Title order={2} c="blue">{formattedBalance} tokens</Title>
            <Text size="sm" c="dimmed">Hết hạn: {formattedExpiry}</Text>
            
            {userToken?.subscription && (
              <Stack mt="sm">
                <Text size="sm">
                  Gói đăng ký: <Text span fw={500}>{userToken.subscription.packageInfo.name}</Text>
                </Text>
                {userToken.subscription.packageInfo.monthlyRenewal && (
                  <Text size="sm">
                    Ngày cấp token tiếp theo: <Text span fw={500}>
                      {new Date(userToken.subscription.nextRenewalDate).toLocaleDateString('vi-VN')}
                    </Text>
                  </Text>
                )}
              </Stack>
            )}
          </div>
        </Group>
      </Paper>

      <Tabs value={activeTab} onChange={setActiveTab} defaultValue="packages">
        <Tabs.List mb="md">
          <Tabs.Tab value="packages" leftSection={<IconCreditCard size={16} />}>
            Mua token
          </Tabs.Tab>
          <Tabs.Tab value="transactions" leftSection={<IconHistory size={16} />}>
            Lịch sử giao dịch
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="packages">
          {isLoading && !filteredPackages.length ? (
            <Group justify="center" py="xl">
              <Loader />
            </Group>
          ) : (
            <Grid>
              {filteredPackages.map((pkg) => (
                <Grid.Col key={pkg._id} span={{ base: 12, sm: 6, md: 4 }}>
                  <Card shadow="sm" p="md" withBorder h="100%">
                    <Stack h="100%">
                      <Badge size="lg" fullWidth variant={pkg.isMonthly ? "light" : "filled"}>
                        {pkg.isMonthly ? 'Gói tháng' : 'Gói năm'}
                      </Badge>
                      
                      <Title order={3}>{pkg.name}</Title>
                      <Text size="xl" fw={700} c="blue">
                        {formatPrice(pkg.price)}
                      </Text>
                      
                      {pkg.discountPercent > 0 && (
                        <Text size="sm" c="green">
                          Tiết kiệm {pkg.discountPercent}%
                        </Text>
                      )}
                      
                      <Divider />
                      
                      <Group justify="space-between">
                        <Text>Số token:</Text>
                        <Text fw={500}>{pkg.tokenAmount.toLocaleString()}</Text>
                      </Group>
                      
                      <Group justify="space-between">
                        <Text>Thời hạn:</Text>
                        <Text fw={500}>{pkg.isMonthly ? '1 tháng' : '1 năm'}</Text>
                      </Group>
                      
                      {pkg.monthlyRenewal && (
                        <Text size="sm" c="dimmed">
                          Token được cấp hàng tháng
                        </Text>
                      )}
                      
                      <Button 
                        mt="auto"
                        fullWidth 
                        onClick={() => handleBuyPackage(pkg._id)}
                        loading={isLoading}
                      >
                        Mua ngay
                      </Button>
                    </Stack>
                  </Card>
                </Grid.Col>
              ))}
            </Grid>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="transactions">
          {isLoading ? (
            <Group justify="center" py="xl">
              <Loader />
            </Group>
          ) : transactions.length === 0 ? (
            <Alert color="gray">
              Bạn chưa có giao dịch nào.
            </Alert>
          ) : (
            <>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Ngày</Table.Th>
                    <Table.Th>Gói</Table.Th>
                    <Table.Th>Số tiền</Table.Th>
                    <Table.Th>Trạng thái</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {transactions.map((transaction) => (
                    <Table.Tr key={transaction.id}>
                      <Table.Td>{new Date(transaction.date).toLocaleDateString('vi-VN')}</Table.Td>
                      <Table.Td>{transaction.package?.name || 'N/A'}</Table.Td>
                      <Table.Td>{formatPrice(transaction.amount)}</Table.Td>
                      <Table.Td>
                        <Badge color={getStatusColor(transaction.status)}>
                          {formatStatus(transaction.status)}
                        </Badge>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
              
              {pagination.pages > 1 && (
                <Group justify="center" mt="md">
                  <Button 
                    variant="outline" 
                    disabled={pagination.page === 1}
                    onClick={() => fetchTransactions(pagination.page - 1)}
                  >
                    Trang trước
                  </Button>
                  <Text>
                    Trang {pagination.page} / {pagination.pages}
                  </Text>
                  <Button 
                    variant="outline" 
                    disabled={pagination.page === pagination.pages}
                    onClick={() => fetchTransactions(pagination.page + 1)}
                  >
                    Trang sau
                  </Button>
                </Group>
              )}
            </>
          )}
        </Tabs.Panel>
      </Tabs>

      {/* Modal thanh toán QR */}
      <Modal
        opened={paymentModalOpen}
        onClose={closePaymentModal}
        title="Quét mã để thanh toán"
        size="md"
        centered
      >
        {paymentData && (
          <Stack align="center" gap="md">
            <QRCodeSVG value={paymentData.qrCode} size={250} />
            
            <Text ta="center" size="lg" fw={500}>
              {formatPrice(paymentData.amount)}
            </Text>
            
            <Alert icon={<IconInfoCircle size={16} />} color="blue">
              Quét mã QR bằng ứng dụng ngân hàng để thanh toán. Sau khi thanh toán, token sẽ được cộng vào tài khoản của bạn.
            </Alert>
            
            <Group justify="center">
              <Button 
                component="a" 
                href={paymentData.paymentUrl} 
                target="_blank"
                variant="outline"
              >
                Mở trang thanh toán
              </Button>
              <Button onClick={() => checkPaymentStatus(paymentData.orderCode)}>
                Kiểm tra trạng thái
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* Modal thành công */}
      <Modal
        opened={successModalOpen}
        onClose={() => setSuccessModalOpen(false)}
        title="Thanh toán thành công"
        size="md"
        centered
      >
        <Stack gap="md">
          <Alert color="green">
            Bạn đã thanh toán thành công. Token đã được cộng vào tài khoản.
          </Alert>
          
          <Group justify="space-between">
            <Text>Số token hiện có:</Text>
            <Text fw={700}>{formattedBalance}</Text>
          </Group>
          
          <Group justify="space-between">
            <Text>Hết hạn:</Text>
            <Text fw={500}>{formattedExpiry}</Text>
          </Group>
          
          <Button fullWidth onClick={() => setSuccessModalOpen(false)}>
            Đóng
          </Button>
        </Stack>
      </Modal>

      {/* Modal cập nhật trạng thái */}
      <Modal
        opened={updateStatusModal.open}
        onClose={closeUpdateStatusModal}
        title="Cập nhật trạng thái giao dịch"
        size="md"
        centered
      >
        {updateStatusModal.transaction && (
          <Stack gap="md">
            <Text>Giao dịch: {updateStatusModal.transaction.paymentId}</Text>
            <Text>Gói: {updateStatusModal.transaction.package?.name}</Text>
            <Text>Số tiền: {formatPrice(updateStatusModal.transaction.amount)}</Text>
            
            <Select
              label="Trạng thái mới"
              value={updateStatusModal.status}
              onChange={(value) => updateStatusModalState(value || '')}
              data={[
                { value: PaymentStatus.PAID, label: 'Đã thanh toán (PAID)' },
                { value: PaymentStatus.COMPLETED, label: 'Hoàn thành (COMPLETED)' },
                { value: PaymentStatus.FAILED, label: 'Thất bại (FAILED)' },
                { value: PaymentStatus.CANCELLED, label: 'Đã hủy (CANCELLED)' }
              ]}
            />
            
            <Alert color="yellow" title="Lưu ý">
              Nếu chọn "Đã thanh toán" hoặc "Hoàn thành", token sẽ tự động được cộng vào tài khoản người dùng.
            </Alert>
            
            <Group justify="space-between">
              <Button variant="outline" onClick={closeUpdateStatusModal}>
                Hủy
              </Button>
              <Button onClick={handleUpdateStatus} color="blue">
                Cập nhật
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Container>
  );
} 